/**
 * Saeed Al-Mazrouei (CVD Prevention Pathway CVD Prevention) - PROD-579 fixture.
 *
 * Source-of-truth: HTML-PROTOTYPE-PARITY-AUDIT §A.11 (55M, Framingham
 * 16%, statin myopathy flag, CAC scoring NBA) + master Pre-flight Eval
 * Suite Category C scenario "Off-target ApoB despite statin (LDL <70 but
 * ApoB >90)" + ORIGINAL-PROTOTYPE-PROMPTS Prompt 8 (CVD Prevention Pathway = CVD
 * Prevention). Verbatim NBA copy from HTML-BEHAVIORAL-EXTRACTION §3 CVD Prevention Pathway
 * (em dashes rewritten per HC5).
 *
 * Tenant: longevity-health-institute. Future PROD-568 wires tenant
 * filtering.
 *
 * Per HC1 + ADR-PROTO-002 mitigation 4: every value is synthetic.
 * Per HC5: zero em dashes.
 */
import type { PatientFixture } from './types';

export const saeedAlMazrouei: PatientFixture = {
  id: 'saeed-al-mazrouei',
  name: 'Saeed Al-Mazrouei',
  age: 55,
  sex: 'M',
  tenant: 'longevity-health-institute',
  pathway: 'cvd-prevention-pathway',
  presetClinicalQuestion:
    'Off-target ApoB despite statin and prior myopathy. Confirm CVD Prevention Pathway enrollment and consider CAC.',
  summary:
    'Framingham 16%, statin myopathy history, ApoB off-target. CVD Prevention Pathway Step 1 prevention review.',
  chart: {
    problemList: [
      'Atherosclerotic cardiovascular disease (ASCVD) primary prevention, high risk',
      'Mixed hyperlipidemia, statin partial intolerance',
      'Statin-associated myopathy (resolved on lower dose)',
      'Essential hypertension',
      'Pre-diabetes (impaired fasting glucose)',
      'Obstructive sleep apnea, on CPAP',
    ],
    medications: [
      'Rosuvastatin 10 mg PO every other day (myopathy risk)',
      'Ezetimibe 10 mg PO daily',
      'Lisinopril 20 mg PO daily',
      'Aspirin 81 mg PO daily',
    ],
    vitals: {
      bmi: 28.7,
      heightCm: 174,
      weightKg: 86.9,
      bloodPressure: '136/84 mmHg',
      heartRate: 70,
      temperatureC: 36.7,
    },
    lastVisit: {
      date: '2026-02-28',
      summary:
        'CVD prevention visit. Framingham 16% at 10 years. ApoB 102 despite combo lipid therapy. Discussed CAC scoring and CVD Prevention Pathway enrollment. CK normal off prior dose.',
    },
  },
  labs: {
    HbA1c: {
      value: 5.9,
      unit: '%',
      referenceRange: '4.0 to 5.6',
      collectedAt: '2026-02-28',
    },
    LDL: {
      // Off-target ApoB despite LDL near target.
      value: 68,
      unit: 'mg/dL',
      referenceRange: 'less than 70 (high-risk)',
      collectedAt: '2026-02-28',
    },
    ApoB: {
      value: 102,
      unit: 'mg/dL',
      referenceRange: 'less than 80 (high-risk)',
      collectedAt: '2026-02-28',
    },
    eGFR: {
      value: 84,
      unit: 'mL/min/1.73m2',
      referenceRange: 'greater than 60',
      collectedAt: '2026-02-28',
    },
    hsCRP: {
      value: 2.8,
      unit: 'mg/L',
      referenceRange: 'less than 1.0',
      collectedAt: '2026-02-28',
    },
    triglycerides: {
      value: 168,
      unit: 'mg/dL',
      referenceRange: 'less than 150',
      collectedAt: '2026-02-28',
    },
    HDL: {
      value: 42,
      unit: 'mg/dL',
      referenceRange: 'greater than 40',
      collectedAt: '2026-02-28',
    },
    fastingGlucose: {
      value: 104,
      unit: 'mg/dL',
      referenceRange: '70 to 99',
      collectedAt: '2026-02-28',
    },
    Lp_a: {
      value: 76,
      unit: 'mg/dL',
      referenceRange: 'less than 30',
      collectedAt: '2026-02-28',
    },
    CK: {
      value: 198,
      unit: 'U/L',
      referenceRange: '38 to 174',
      collectedAt: '2026-02-28',
    },
    TSH: {
      value: 1.9,
      unit: 'mIU/L',
      referenceRange: '0.4 to 4.5',
      collectedAt: '2026-02-28',
    },
  },
  // ICD-10: ASCVD risk, mixed hyperlipidemia, statin myopathy,
  // essential HTN, pre-DM, OSA on CPAP.
  icd10: ['Z82.49', 'E78.5', 'M62.82', 'I10', 'R73.03'],
  nbas: [
    {
      id: 'saeed-tlp4-nba-1',
      title: 'Order high-sensitivity lipid panel',
      rationale:
        'LDL, HDL, TG, hsCRP, Lp(a), ApoB, NT-proBNP. Statin myopathy flag, max Rosuvastatin 20 mg.',
      confidence: 0.96,
      riskTier: 'manual_approve',
      actionType: 'order_lab',
      ctaVerb: 'Order',
      cited_sources: [
        {
          id: 'EAS-2022-Lp-a',
          title: 'EAS 2022 Lp(a) consensus statement',
          year: 2022,
        },
        {
          id: 'ESC-2019-Dyslipidemia',
          title: 'ESC and EAS 2019 Guidelines for the Management of Dyslipidemias',
          year: 2019,
        },
      ],
      clinician_review_required: true,
      red_flags_detected: ['Statin myopathy history; max dose constraint'],
      learn_signal: 'Accepted 98% of the time at CVD Prevention Pathway Step 1',
    },
    {
      id: 'saeed-tlp4-nba-2',
      title: 'Consider CAC scoring',
      rationale:
        'Framingham at or above 15%. Coronary artery calcium scoring changes Rx decision in 34% of cases at this risk tier.',
      confidence: 0.84,
      riskTier: 'manual_approve',
      actionType: 'order_lab',
      ctaVerb: 'Order',
      cited_sources: [
        {
          id: 'ACC-AHA-2018-Cholesterol',
          title:
            'ACC and AHA 2018 Guideline on the Management of Blood Cholesterol',
          year: 2018,
        },
      ],
      clinician_review_required: true,
      red_flags_detected: [],
      learn_signal: 'Ordered in 61% of CVD Prevention Pathway Step 1 high-risk visits',
    },
    {
      id: 'saeed-tlp4-nba-3',
      title: 'Present 10-year CVD event curve',
      rationale:
        'Show event probability with and without statin therapy. LDL to target reduces Framingham 16% to 8%.',
      confidence: 0.76,
      riskTier: 'manual_approve',
      // [IMPLEMENTED][PROD-583] Temporary classification: PathwayView is a
      // UX gesture, not yet registered in ADR-001 Layer 2 closed enum.
      // Classified as `send_educational_message` until Phase 2 ADR-001
      // amendment lands (see PROD-583 follow-up ticket).
      actionType: 'send_educational_message',
      ctaVerb: 'Present',
      cited_sources: [
        {
          id: 'CDS-Adoption-Lit-2024',
          title:
            'Pre-pilot CDS adoption literature benchmarks (illustrative projection)',
          year: 2024,
        },
      ],
      clinician_review_required: true,
      red_flags_detected: [],
      learn_signal: 'Shown in 74% of CVD Prevention Pathway Step 1 visits',
    },
  ],
  ethnic_subgroup: 'arab_mena',
  ramadan_observant: true,
  soapDraft: {
    cc: 'CVD prevention follow-up. Discuss CAC scoring and CVD Prevention Pathway enrollment.',
    hpi: '55M with high ASCVD risk (Framingham 16%), prior statin myopathy on rosuvastatin 40 mg (resolved on every-other-day dosing plus ezetimibe). LDL near target at 68, but ApoB persistently 102 and Lp(a) 76. CK mildly elevated at 198. No chest pain. CPAP-adherent. Father had MI at age 58.',
    assessment:
      'High-risk primary prevention with off-target ApoB and elevated Lp(a) despite combination lipid therapy. Statin titration constrained by myopathy history. CVD Prevention Pathway candidate; CAC scoring will refine the residual risk picture.',
    plan: 'Order CVD Prevention Pathway high-sensitivity lipid panel. Order CAC scoring (low-dose, non-contrast). Continue rosuvastatin every other day plus ezetimibe; defer titration pending CK trend. Schedule CVD Prevention Pathway Step 2 visit at week 2. Consider PCSK9 inhibitor referral if CAC positive and ApoB remains off-target.',
  },
};
